import React, { useEffect, useState } from "react";
import {
    HeartOutlined,
    SaveOutlined,
    DeleteOutlined,
    ArrowLeftOutlined
} from "@ant-design/icons";

import {
    canSyncHealthData,
    getHealthHistory,
    saveHealthData,
    deleteHealthData
} from "../../api/healthTracking";

export default function HuyetAp() {
    const [systolic, setSystolic] = useState("");
    const [diastolic, setDiastolic] = useState("");
    const [heartRate, setHeartRate] = useState("");
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const syncWithAccount = canSyncHealthData();


    /*
     * ĐỌC DỮ LIỆU
     *
     * Có JWT:
     * lấy lịch sử từ database theo tài khoản.
     *
     * Không có JWT:
     * lấy dữ liệu localStorage như phiên bản cũ.
     */
    useEffect(() => {
        const loadHistory = async () => {
            setLoading(true);

            if (syncWithAccount) {
                const result = await getHealthHistory("huyet_ap");

                if (result.success) {
                    const data = result.data.map((item) => ({
                        id: item.health_tracking_id,
                        systolic: item.tracking_data?.systolic,
                        diastolic: item.tracking_data?.diastolic,
                        heartRate: item.tracking_data?.heartRate,
                        date: item.tracking_data?.date,
                        time: item.tracking_data?.time,
                        database: true
                    }));

                    setHistory(data);
                } else {
                    setHistory([]);
                }
            } else {
                const savedData = localStorage.getItem(
                    "huyetApHistory"
                );

                if (savedData) {
                    try {
                        setHistory(JSON.parse(savedData));
                    } catch (error) {
                        setHistory([]);
                    }
                } else {
                    setHistory([]);
                }
            }

            setLoading(false);
        };

        loadHistory();
    }, [syncWithAccount]);


    /*
     * TẢI LẠI DỮ LIỆU TỪ DATABASE
     */
    const reloadAccountHistory = async () => {
        const result = await getHealthHistory("huyet_ap");

        if (!result.success) {
            return false;
        }

        const data = result.data.map((item) => ({
            id: item.health_tracking_id,
            systolic: item.tracking_data?.systolic,
            diastolic: item.tracking_data?.diastolic,
            heartRate: item.tracking_data?.heartRate,
            date: item.tracking_data?.date,
            time: item.tracking_data?.time,
            database: true
        }));

        setHistory(data);

        return true;
    };


    /*
     * LƯU CHỈ SỐ
     */
    const handleSave = async () => {
        if (!systolic || !diastolic) {
            alert(
                "Vui lòng nhập huyết áp tâm thu và tâm trương."
            );
            return;
        }

        const systolicNumber = Number(systolic);
        const diastolicNumber = Number(diastolic);
        const heartRateNumber = heartRate
            ? Number(heartRate)
            : null;

        if (
            systolicNumber <= 0 ||
            diastolicNumber <= 0 ||
            (heartRate && heartRateNumber <= 0)
        ) {
            alert("Vui lòng nhập chỉ số hợp lệ.");
            return;
        }

        const now = new Date();

        const newRecord = {
            id: Date.now(),
            systolic: systolicNumber,
            diastolic: diastolicNumber,
            heartRate: heartRateNumber,

            date: now.toLocaleDateString("vi-VN"),

            time: now.toLocaleTimeString("vi-VN", {
                hour: "2-digit",
                minute: "2-digit"
            })
        };


        /*
         * ĐÃ ĐĂNG NHẬP CÓ JWT
         * → lưu database
         */
        if (syncWithAccount) {
            setSaving(true);

            const result = await saveHealthData(
                "huyet_ap",
                {
                    systolic: systolicNumber,
                    diastolic: diastolicNumber,
                    heartRate: heartRateNumber,
                    date: newRecord.date,
                    time: newRecord.time
                }
            );

            if (!result.success) {
                setSaving(false);

                alert(
                    "Không thể lưu dữ liệu vào tài khoản. Vui lòng thử lại."
                );

                return;
            }

            await reloadAccountHistory();

            setSaving(false);
        }

        /*
         * KHÔNG CÓ JWT
         * → vẫn lưu localStorage như trước
         */
        else {
            const newHistory = [
                newRecord,
                ...history
            ];

            setHistory(newHistory);

            localStorage.setItem(
                "huyetApHistory",
                JSON.stringify(newHistory)
            );
        }


        setSystolic("");
        setDiastolic("");
        setHeartRate("");
    };


    /*
     * XÓA CHỈ SỐ
     */
    const handleDelete = async (id) => {

        /*
         * DỮ LIỆU THEO TÀI KHOẢN
         */
        if (syncWithAccount) {
            const result = await deleteHealthData(id);

            if (!result.success) {
                alert(
                    "Không thể xóa dữ liệu. Vui lòng thử lại."
                );
                return;
            }

            await reloadAccountHistory();
        }

        /*
         * DỮ LIỆU LOCAL
         */
        else {
            const newHistory = history.filter(
                (item) => item.id !== id
            );

            setHistory(newHistory);

            localStorage.setItem(
                "huyetApHistory",
                JSON.stringify(newHistory)
            );
        }
    };


    return (
        <div className="max-w-screen-xl mx-auto px-3 md:px-5 pt-16 md:pt-4 pb-10">

            {/* QUAY LẠI */}
            <a
                href="/tienich/suc-khoe"
                className="inline-flex items-center text-blue-600 font-semibold text-sm mb-4"
            >
                <ArrowLeftOutlined className="mr-2" />
                Theo dõi sức khỏe
            </a>


            {/* PHẦN ĐẦU */}
            <div className="bg-gradient-to-r from-red-500 to-rose-500 rounded-2xl px-5 py-5 md:p-8 text-white">

                <div className="flex items-center">

                    <div className="w-12 h-12 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-2xl">
                        <HeartOutlined />
                    </div>

                    <div className="ml-4">

                        <p className="text-[11px] md:text-sm font-semibold opacity-90">
                            THEO DÕI SỨC KHỎE
                        </p>

                        <h1 className="text-[25px] md:text-3xl font-bold">
                            Huyết áp
                        </h1>

                    </div>

                </div>

                <p className="text-[14px] md:text-base mt-4 opacity-90 leading-5 md:leading-normal">
                    Ghi lại huyết áp và nhịp tim để theo dõi sự thay đổi theo thời gian.
                </p>

            </div>


            {/* TRẠNG THÁI LƯU DỮ LIỆU */}
            {syncWithAccount ? (

                <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-3 mt-4">

                    <p className="text-[13px] text-green-700 font-semibold">
                        ✓ Dữ liệu đang được lưu theo tài khoản
                    </p>

                    <p className="text-[12px] text-green-600 mt-1">
                        Bạn có thể đăng nhập tài khoản này trên thiết bị khác để xem lại lịch sử.
                    </p>

                </div>

            ) : (

                <div className="bg-yellow-50 border border-yellow-100 rounded-xl px-4 py-3 mt-4">

                    <p className="text-[13px] text-yellow-700 font-semibold">
                        Dữ liệu đang được lưu trên thiết bị này
                    </p>

                    <p className="text-[12px] text-yellow-600 mt-1">
                        Đăng nhập bằng tài khoản có mật khẩu để lưu lịch sử theo tài khoản.
                    </p>

                </div>

            )}


            {/* FORM NHẬP */}
            <div className="bg-white border rounded-2xl p-4 md:p-6 mt-5">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    Nhập chỉ số
                </h2>

                <p className="text-gray-500 text-sm mt-1">
                    Nhập kết quả đo huyết áp hiện tại của bạn.
                </p>


                <div className="grid grid-cols-2 gap-3 mt-5">

                    {/* TÂM THU */}
                    <div>

                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Tâm thu
                        </label>

                        <div className="relative">

                            <input
                                type="number"
                                value={systolic}
                                onChange={(e) =>
                                    setSystolic(
                                        e.target.value
                                    )
                                }
                                placeholder="120"
                                className="w-full h-12 border rounded-xl px-3 pr-14 outline-none focus:border-blue-500"
                            />

                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                                mmHg
                            </span>

                        </div>

                    </div>


                    {/* TÂM TRƯƠNG */}
                    <div>

                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Tâm trương
                        </label>

                        <div className="relative">

                            <input
                                type="number"
                                value={diastolic}
                                onChange={(e) =>
                                    setDiastolic(
                                        e.target.value
                                    )
                                }
                                placeholder="80"
                                className="w-full h-12 border rounded-xl px-3 pr-14 outline-none focus:border-blue-500"
                            />

                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                                mmHg
                            </span>

                        </div>

                    </div>

                </div>


                {/* NHỊP TIM */}
                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nhịp tim
                    </label>

                    <div className="relative">

                        <input
                            type="number"
                            value={heartRate}
                            onChange={(e) =>
                                setHeartRate(
                                    e.target.value
                                )
                            }
                            placeholder="Ví dụ: 72"
                            className="w-full h-12 border rounded-xl px-3 pr-24 outline-none focus:border-blue-500"
                        />

                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                            lần/phút
                        </span>

                    </div>

                </div>


                {/* NÚT LƯU */}
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className={`w-full mt-5 h-12 rounded-xl text-white font-bold ${
                        saving
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                    }`}
                >

                    <SaveOutlined className="mr-2" />

                    {saving
                        ? "Đang lưu..."
                        : "Lưu chỉ số"}

                </button>

            </div>


            {/* LỊCH SỬ */}
            <div className="mt-7">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    Lịch sử gần đây
                </h2>

                <p className="text-gray-500 text-sm mt-1">
                    {syncWithAccount
                        ? "Các chỉ số được lưu theo tài khoản của bạn."
                        : "Các chỉ số bạn đã lưu trên thiết bị này."}
                </p>


                {loading ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <p className="text-sm text-gray-500">
                            Đang tải dữ liệu...
                        </p>

                    </div>

                ) : history.length === 0 ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <HeartOutlined className="text-3xl text-gray-300" />

                        <p className="font-semibold text-gray-700 mt-3">
                            Chưa có dữ liệu
                        </p>

                        <p className="text-sm text-gray-500 mt-1">
                            Chỉ số huyết áp bạn lưu sẽ xuất hiện tại đây.
                        </p>

                    </div>

                ) : (

                    <div className="space-y-3 mt-4">

                        {history.map((item) => (

                            <div
                                key={item.id}
                                className="bg-white border rounded-2xl p-4 flex items-center justify-between"
                            >

                                <div>

                                    <div className="flex items-end">

                                        <p className="text-2xl font-bold text-gray-900">
                                            {item.systolic}/{item.diastolic}
                                        </p>

                                        <span className="text-xs text-gray-400 ml-2 mb-1">
                                            mmHg
                                        </span>

                                    </div>


                                    {item.heartRate && (

                                        <p className="text-sm text-gray-600 mt-1">
                                            Nhịp tim: {item.heartRate} lần/phút
                                        </p>

                                    )}


                                    <p className="text-xs text-gray-400 mt-1">
                                        {item.time} - {item.date}
                                    </p>

                                </div>


                                <button
                                    onClick={() =>
                                        handleDelete(
                                            item.id
                                        )
                                    }
                                    className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center"
                                    aria-label="Xóa chỉ số"
                                >
                                    <DeleteOutlined />
                                </button>

                            </div>

                        ))}

                    </div>

                )}

            </div>


            {/* LƯU Ý */}
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mt-7">

                <p className="font-semibold text-gray-800 text-sm">
                    Lưu ý
                </p>

                {syncWithAccount ? (

                    <p className="text-[13px] text-gray-600 leading-5 mt-1">
                        Dữ liệu huyết áp được lưu theo tài khoản đang đăng nhập và có thể được tải lại trên thiết bị khác khi sử dụng cùng tài khoản.
                    </p>

                ) : (

                    <p className="text-[13px] text-gray-600 leading-5 mt-1">
                        Dữ liệu hiện được lưu trên trình duyệt của thiết bị bạn đang sử dụng.
                        Nếu xóa dữ liệu trình duyệt hoặc đổi thiết bị, lịch sử có thể không còn.
                    </p>

                )}

                <p className="text-[13px] text-gray-500 leading-5 mt-2">
                    Tiện ích này dùng để ghi lại và theo dõi thông tin sức khỏe,
                    không thay thế việc đánh giá hoặc tư vấn của nhân viên y tế.
                </p>

            </div>

        </div>
    );
}